import React, { useState, useCallback } from "react";
import { Image as ImageIcon, Video, Copy, Check, Loader2, FileText } from "lucide-react";
import { useTheme } from "@context/ThemeContext";
import RefImagePreview from "./RefImagePreview";
import { getPublicVideoLabel } from "../../utils/projectRuntime";

/**
 * 单条生成日志
 */
const GenerationLogItem = React.memo(({ log }) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [hoverRef, setHoverRef] = useState(null); // { url, el }

  const isImage = log.type === "IMAGE";
  const isVideo = log.type === "VIDEO";
  const isText = log.type === "TEXT";
  const refs = parseRefs(log.reference_images);
  const timeStr = formatRelativeTime(log.created_at);

  const handleCopy = useCallback((e) => {
    e.stopPropagation();
    const text = log.final_prompt || log.user_prompt || "";
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, [log.final_prompt, log.user_prompt]);

  const statusDotColor = log.status === "success"
    ? "var(--color-success)"
    : log.status === "failed"
      ? "var(--color-error)"
      : "var(--color-warning)";
  const statusDotPulse = log.status !== "success" && log.status !== "failed";

  return (
    <div
      className="flex gap-3 p-3 rounded-lg transition-colors duration-200 border border-transparent hover:border-[var(--color-border-primary)]"
      style={{ backgroundColor: "var(--color-bg-primary)" }}
    >
      {/* 左侧缩略图 */}
      <div
        className="w-12 h-12 rounded-lg overflow-hidden shrink-0 cursor-pointer relative"
        style={{ backgroundColor: "black" }}
        onClick={() => log.result_url && window.open(log.result_url, "_blank")}
        title="点击预览"
      >
        {log.result_url ? (
          isImage ? (
            <img src={log.result_url} className="w-full h-full object-cover" loading="lazy" />
          ) : (
            <video src={log.result_url} preload="none" className="w-full h-full object-cover" muted
              onMouseOver={(e) => e.target.play()}
              onMouseOut={(e) => e.target.pause()} />
          )
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            {log.status === "pending" ? (
              <Loader2 size={16} className="animate-spin" style={{ color: "var(--color-text-muted)" }} />
            ) : (
            isImage ? <ImageIcon size={16} style={{ color: "var(--color-text-muted)" }} /> : isVideo ? <Video size={16} style={{ color: "var(--color-text-muted)" }} /> : <FileText size={16} style={{ color: "var(--color-text-muted)" }} />
            )}
          </div>
        )}
        {/* 状态点 */}
        <div className={`absolute top-1 right-1 w-2 h-2 rounded-full ${statusDotPulse ? "animate-pulse" : ""}`} style={{ backgroundColor: statusDotColor }} />
      </div>

      {/* 右侧内容 */}
      <div className="flex-1 min-w-0 flex flex-col gap-1">
        {/* 第一行: 类型 + provider + 时间 */}
        <div className="flex items-center gap-1.5 text-2xs" style={{ color: "var(--color-text-muted)" }}>
          <span className="px-1.5 py-0.5 rounded font-medium" style={{
            backgroundColor: isImage ? `${theme.colors.bluePrimary}20` : isVideo ? `${theme.colors.purplePrimary}20` : `${theme.colors.accentPrimary}20`,
            color: isImage ? theme.colors.bluePrimary : isVideo ? theme.colors.purplePrimary : theme.colors.accentPrimary,
          }}>
            {isImage ? "图片" : isVideo ? "视频" : isText ? "文本" : log.type}
          </span>
          <span className="px-1.5 py-0.5 rounded" style={{ backgroundColor: "var(--color-bg-tertiary)" }}>
            {getPublicVideoLabel(log.provider || log.videoProvider || log.model || log.source, "AI")}
          </span>
          <span className="ml-auto">{timeStr}</span>
        </div>

        {/* 第二行: 提示词 */}
        <div className="relative">
          <div
            className={`text-2xs leading-relaxed cursor-pointer ${expanded ? "" : "line-clamp-3"}`}
            style={{ color: "var(--color-text-secondary)" }}
            onClick={() => setExpanded(!expanded)}
            title={expanded ? "点击收起" : "点击展开完整提示词"}
          >
            {log.final_prompt || log.user_prompt || "无提示词"}
          </div>
          {(log.final_prompt || log.user_prompt) && (
            <button
              onClick={handleCopy}
              className="absolute top-0 right-0 p-0.5 rounded transition-all duration-200"
              style={{ color: "var(--color-text-muted)" }}
              onMouseEnter={(e) => { e.currentTarget.style.color = theme.colors.accentPrimary; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = "var(--color-text-muted)"; }}
              title="复制提示词"
            >
              {copied ? <Check size={10} style={{ color: theme.colors.accentPrimary }} /> : <Copy size={10} />}
            </button>
          )}
        </div>

        {/* 第三行: 参考图 chips */}
        {refs.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-0.5">
            {refs.map((ref, i) => (
              <div
                key={i}
                className="flex items-center gap-1 px-1.5 py-0.5 rounded-md cursor-default"
                style={{
                  backgroundColor: "var(--color-bg-tertiary)",
                  border: "1px solid var(--color-border-primary)",
                }}
                onMouseEnter={(e) => { if (ref.url) setHoverRef({ url: ref.url, el: e.currentTarget }); }}
                onMouseLeave={() => setHoverRef(null)}
              >
                {ref.url ? (
                  <img src={ref.url} className="w-5 h-5 rounded object-cover" />
                ) : (
                  <div className="w-5 h-5 rounded flex items-center justify-center text-2xs font-bold"
                    style={{ backgroundColor: "var(--color-bg-overlay)", color: "var(--color-text-muted)" }}>
                    {refTypeIcon(ref.type)}
                  </div>
                )}
                <span className="text-2xs truncate max-w-[60px]" style={{ color: "var(--color-text-secondary)" }}>
                  {ref.name || ref.type}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* hover 大图预览 */}
      {hoverRef && <RefImagePreview url={hoverRef.url} anchorEl={hoverRef.el} />}
    </div>
  );
});

function parseRefs(json) {
  if (!json) return [];
  try {
    return JSON.parse(json);
  } catch {
    return [];
  }
}

function refTypeIcon(type) {
  if (type === "character") return "角";
  if (type === "scene") return "景";
  if (type === "prop") return "道";
  return "?";
}

function formatRelativeTime(dateStr) {
  if (!dateStr) return "";
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return "刚刚";
    if (diffMin < 60) return `${diffMin}分钟前`;
    const diffHour = Math.floor(diffMin / 60);
    if (diffHour < 24) return `${diffHour}小时前`;
    const diffDay = Math.floor(diffHour / 24);
    if (diffDay < 7) return `${diffDay}天前`;
    return date.toLocaleDateString("zh-CN", { month: "numeric", day: "numeric" });
  } catch {
    return "";
  }
}

export default GenerationLogItem;
